import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-hot-toast';
import {
  GET_IS_TIME_CLOCK_GLOBALLY_ENABLED,
  GET_TIME_CLOCK_MAX_HOURS_PER_DAY,
  GET_TIME_CLOCK_OFFICE_RADIUS_METERS,
  SET_TIME_CLOCK_GLOBALLY_ENABLED,
  SET_TIME_CLOCK_MAX_HOURS_PER_DAY,
  SET_TIME_CLOCK_OFFICE_RADIUS_METERS,
} from '../../graphql/settings';

const DEFAULT_MAX_HOURS_PER_DAY = 16;
const DEFAULT_OFFICE_RADIUS_METERS = 75;

function TimeClockSettingsSection() {
  const [isGloballyEnabled, setIsGloballyEnabled] = useState(true);
  const [maxHoursPerDay, setMaxHoursPerDay] = useState(DEFAULT_MAX_HOURS_PER_DAY);
  const [officeRadiusMeters, setOfficeRadiusMeters] = useState(DEFAULT_OFFICE_RADIUS_METERS);

  const {
    data: globalEnabledData,
    loading: globalEnabledLoading,
    refetch: refetchGlobalEnabled,
  } = useQuery(GET_IS_TIME_CLOCK_GLOBALLY_ENABLED, {
    fetchPolicy: 'cache-and-network',
  });

  const { data, loading, refetch } = useQuery(GET_TIME_CLOCK_MAX_HOURS_PER_DAY, {
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: radiusData,
    loading: radiusLoading,
    refetch: refetchRadius,
  } = useQuery(GET_TIME_CLOCK_OFFICE_RADIUS_METERS, {
    fetchPolicy: 'cache-and-network',
  });

  const [updateMaxHours, { loading: saving }] = useMutation(SET_TIME_CLOCK_MAX_HOURS_PER_DAY, {
    onCompleted: (result) => {
      if (result?.setTimeClockMaxHoursPerDay) {
        toast.success('Timeclock settings updated');
        refetch();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update timeclock settings');
    },
  });

  const [updateGlobalEnabled, { loading: savingGlobalEnabled }] = useMutation(SET_TIME_CLOCK_GLOBALLY_ENABLED, {
    onError: (error) => {
      toast.error(error.message || 'Failed to update timeclock availability');
    },
  });

  const [updateOfficeRadius, { loading: savingRadius }] = useMutation(SET_TIME_CLOCK_OFFICE_RADIUS_METERS, {
    onCompleted: (result) => {
      if (result?.setTimeClockOfficeRadiusMeters) {
        toast.success('Timeclock office radius updated');
        refetchRadius();
      }
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to update timeclock office radius');
    },
  });

  useEffect(() => {
    if (globalEnabledData?.isTimeClockGloballyEnabled !== undefined) {
      setIsGloballyEnabled(globalEnabledData.isTimeClockGloballyEnabled);
    }
  }, [globalEnabledData]);

  useEffect(() => {
    if (data?.timeClockMaxHoursPerDay) {
      setMaxHoursPerDay(data.timeClockMaxHoursPerDay);
    }
  }, [data]);

  useEffect(() => {
    if (radiusData?.timeClockOfficeRadiusMeters) {
      setOfficeRadiusMeters(radiusData.timeClockOfficeRadiusMeters);
    }
  }, [radiusData]);

  const handleSave = async () => {
    const parsedHours = parseInt(maxHoursPerDay, 10);
    if (Number.isNaN(parsedHours) || parsedHours < 1 || parsedHours > 24) {
      toast.error('Max hours per day must be between 1 and 24');
      return;
    }

    await updateMaxHours({
      variables: {
        hours: parsedHours,
      },
    });
  };

  const handleToggleGlobalEnabled = async (enabled) => {
    const previousValue = isGloballyEnabled;
    try {
      setIsGloballyEnabled(enabled);
      const result = await updateGlobalEnabled({
        variables: {
          enabled,
        },
      });
      if (result?.data?.setTimeClockGloballyEnabled) {
        toast.success(`Timeclock ${enabled ? 'enabled' : 'disabled'} globally`);
        refetchGlobalEnabled();
      }
    } catch {
      setIsGloballyEnabled(previousValue);
    }
  };

  const handleSaveRadius = async () => {
    const parsedMeters = parseInt(officeRadiusMeters, 10);
    if (Number.isNaN(parsedMeters) || parsedMeters < 1 || parsedMeters > 5000) {
      toast.error('Office radius must be between 1 and 5000 meters');
      return;
    }

    await updateOfficeRadius({
      variables: {
        meters: parsedMeters,
      },
    });
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-lg font-medium text-gray-900">Timeclock Settings</h2>
        <p className="mt-1 text-sm text-gray-500">
          Configure timeclock rules for hourly team members.
        </p>
      </div>

      <div className="space-y-6">
        <div className="border-b border-gray-200 pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h3 className="text-md font-medium text-gray-700">Global Availability</h3>
              <p className="mt-1 text-sm text-gray-500">
                Turn off timeclock punch tracking for all hourly team members without changing individual employee checkboxes or rates.
              </p>
            </div>

            <button
              type="button"
              role="switch"
              aria-checked={isGloballyEnabled}
              onClick={() => handleToggleGlobalEnabled(!isGloballyEnabled)}
              disabled={globalEnabledLoading || savingGlobalEnabled}
              className={`relative inline-flex h-7 w-12 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60 ${
                isGloballyEnabled ? 'bg-sky-600' : 'bg-gray-300'
              }`}
            >
              <span className="sr-only">Toggle global timeclock availability</span>
              <span
                aria-hidden="true"
                className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  isGloballyEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          <p className={`mt-3 text-sm font-medium ${isGloballyEnabled ? 'text-emerald-700' : 'text-amber-700'}`}>
            {isGloballyEnabled
              ? 'Timeclock is currently available to employees who have it enabled.'
              : 'Timeclock is currently turned off for employees.'}
          </p>
        </div>

        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700">Clock-In Location</h3>
          <p className="mt-1 text-sm text-gray-500">
            Punches are matched to the nearest configured GPS office. If the punch is farther than this radius, it is flagged outside radius.
          </p>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label htmlFor="time-clock-office-radius" className="block text-sm font-medium text-gray-700">
                Office Radius
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="time-clock-office-radius"
                  type="number"
                  min="1"
                  max="5000"
                  step="1"
                  value={officeRadiusMeters}
                  onChange={(event) => setOfficeRadiusMeters(event.target.value)}
                  className="block w-28 rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                  disabled={radiusLoading || savingRadius}
                />
                <span className="text-sm text-gray-500">meters</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">Default is 75 meters. The GPS map office radius is currently 400 meters.</p>
            </div>

            <button
              type="button"
              onClick={handleSaveRadius}
              disabled={radiusLoading || savingRadius}
              className="inline-flex w-fit items-center rounded-md border border-transparent bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {savingRadius ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        <div className="border-b border-gray-200 pb-6">
          <h3 className="text-md font-medium text-gray-700">Auto Clock-Out</h3>
          <p className="mt-1 text-sm text-gray-500">
            Max hours per day is used as the limit for missed clock-out automation.
          </p>

          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
            <div>
              <label htmlFor="time-clock-max-hours" className="block text-sm font-medium text-gray-700">
                Max Hours Per Day
              </label>
              <div className="mt-1 flex items-center gap-2">
                <input
                  id="time-clock-max-hours"
                  type="number"
                  min="1"
                  max="24"
                  step="1"
                  value={maxHoursPerDay}
                  onChange={(event) => setMaxHoursPerDay(event.target.value)}
                  className="block w-28 rounded-md border-gray-300 shadow-sm focus:border-sky-500 focus:ring-sky-500 sm:text-sm"
                  disabled={loading || saving}
                />
                <span className="text-sm text-gray-500">hours</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleSave}
              disabled={loading || saving}
              className="inline-flex w-fit items-center rounded-md border border-transparent bg-sky-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        <div>
          <h3 className="text-md font-medium text-gray-700">Overtime Rules</h3>
          <p className="mt-1 text-sm text-gray-500">Overtime configuration will be added later.</p>
        </div>
      </div>
    </div>
  );
}

export default TimeClockSettingsSection;
